import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  School as AcademyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  UploadFile as UploadFileIcon,
  ExitToApp as LogoutIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate } from 'react-router-dom';
import { COUNTRIES } from '@utils/countries';
import * as XLSX from 'xlsx';

interface Academy {
  id: string;
  academy_id_code?: string;
  name: string;
  description: string;
  country?: string;
  city?: string;
  address: string;
  contact_email: string;
  contact_phone: string;
  status?: string;
}

const AdminAcademyManagement: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [academies, setAcademies] = useState<Academy[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [editingAcademy, setEditingAcademy] = useState<Academy | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    country: '',
    city: '',
    address: '',
    contact_email: '',
    contact_phone: '',
    status: 'active',
  });

  useEffect(() => {
    loadAcademies();
  }, []);

  const loadAcademies = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/academies');
      const data = await response.json();
      
      if (data.success) {
        setAcademies(data.data);
      } else {
        setError(data.message || 'Failed to load academies');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load academies');
    } finally {
      setLoading(false);
    }
  };

  const handleExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);

          // Map Excel columns to academy fields
          const academies = jsonData.map((row: any) => ({
            name: row['Name'] || row['name'] || '',
            description: row['Description'] || row['description'] || '',
            country: row['Country'] || row['country'] || '',
            city: row['City'] || row['city'] || '',
            address: row['Address'] || row['address'] || '',
            contact_email: row['Email'] || row['email'] || row['Contact Email'] || row['contact_email'] || '',
            contact_phone: row['Phone'] || row['phone'] || row['Contact Phone'] || row['contact_phone'] || '',
          }));

          // Filter out rows without name
          const validAcademies = academies.filter((a: any) => a.name);

          if (validAcademies.length === 0) {
            setError('No valid academies found in the Excel file');
            return;
          }

          // Send to backend
          const response = await fetch('/api/academies/bulk', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ academies: validAcademies }),
          });

          const result = await response.json();

          if (result.success) {
            setSuccess(`Successfully imported ${result.data.created.length} academies`);
            if (result.data.errors && result.data.errors.length > 0) {
              setError(`Some rows failed: ${result.data.errors.map((e: any) => `Row ${e.row}: ${e.error}`).join(', ')}`);
            }
            loadAcademies();
            setBulkUploadOpen(false);
          } else {
            setError(result.message || 'Failed to import academies');
          }
        } catch (err: any) {
          setError('Error processing Excel file: ' + err.message);
        } finally {
          setLoading(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (err: any) {
      setError('Error reading file: ' + err.message);
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin');
  };

  const handleOpenDialog = (academy?: Academy) => {
    if (academy) {
      setEditingAcademy(academy);
      setFormData({
        name: academy.name,
        description: academy.description || '',
        country: academy.country || '',
        city: academy.city || '',
        address: academy.address || '',
        contact_email: academy.contact_email || '',
        contact_phone: academy.contact_phone || '',
        status: academy.status || 'active',
      });
    } else {
      setEditingAcademy(null);
      setFormData({
        name: '',
        description: '',
        country: '',
        city: '',
        address: '',
        contact_email: '',
        contact_phone: '',
        status: 'active',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAcademy(null);
    setFormData({
      name: '',
      description: '',
      country: '',
      city: '',
      address: '',
      contact_email: '',
      contact_phone: '',
      status: 'active',
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const url = editingAcademy ? `/api/academies/${editingAcademy.id}` : '/api/academies';
      const method = editingAcademy ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(editingAcademy ? 'Academy updated successfully!' : 'Academy created successfully!');
        handleCloseDialog();
        loadAcademies();
      } else {
        setError(data.message || 'Failed to save academy');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save academy');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (academyId: string) => {
    if (window.confirm('Are you sure you want to delete this academy?')) {
      try {
        setLoading(true);
        const response = await fetch(`/api/academies/${academyId}`, {
          method: 'DELETE',
        });
        
        const data = await response.json();
        
        if (data.success) {
          setSuccess('Academy deleted successfully!');
          loadAcademies();
        } else {
          setError(data.message || 'Failed to delete academy');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to delete academy');
      } finally {
        setLoading(false);
      }
    }
  };


  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back to Admin Dashboard
          </Button>
          <Typography variant="h4" component="h1">
            Academy Management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setBulkUploadOpen(true)}
          >
            Bulk Upload
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Academy
          </Button>
          <Button
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            color="error"
          >
            Logout
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Academies List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Academies
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Academy ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {academies.map((academy) => (
                    <TableRow key={academy.id}>
                      <TableCell>
                        <Chip 
                          label={academy.academy_id_code || 'N/A'} 
                          color="primary" 
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AcademyIcon sx={{ mr: 1, color: 'primary.main' }} />
                          {academy.name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={academy.status === 'permanently_closed' ? 'Permanently Closed' : 'Active'} 
                          color={academy.status === 'permanently_closed' ? 'error' : 'success'} 
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{academy.description || 'N/A'}</TableCell>
                      <TableCell>{academy.country || 'N/A'}</TableCell>
                      <TableCell>{academy.city || 'N/A'}</TableCell>
                      <TableCell>{academy.address || 'N/A'}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{academy.contact_email || 'N/A'}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {academy.contact_phone || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit Academy">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(academy)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Academy">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(academy.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Academy Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAcademy ? 'Edit Academy' : 'Add New Academy'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Academy Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Country</InputLabel>
                <Select
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  label="Country"
                >
                  <MenuItem value="">
                    <em>Select Country</em>
                  </MenuItem>
                  {COUNTRIES.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                multiline
                rows={2}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="permanently_closed">Permanently Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingAcademy ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkUploadOpen} onClose={() => setBulkUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Upload Academies</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Upload an Excel file (.xlsx) with the following columns:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mt: 1 }}>
              <li>Name (required)</li>
              <li>Description</li>
              <li>Country</li>
              <li>City</li>
              <li>Address</li>
              <li>Email / Contact Email</li>
              <li>Phone / Contact Phone</li>
            </Box>
            <Box sx={{ mt: 3 }}>
              <input
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id="excel-upload-input"
                type="file"
                onChange={handleExcelUpload}
              />
              <label htmlFor="excel-upload-input">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<UploadFileIcon />}
                  fullWidth
                >
                  Choose Excel File
                </Button>
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUploadOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminAcademyManagement;
